/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type ExtendedProductReviewImage,
  useProductReviewsImages,
} from './useProductReviewsImages';
import { type ProductReview } from '../interface';

type ReviewImageModalReturnValue = {
  isOpen: boolean;
  hasBackButton: boolean;
  openModal: () => void;
  closeModal: () => void;
  onImageChange: (activeImage: ActiveImage, withBackButton?: boolean) => void;
  activeImage: ActiveImage;
  activeReview: ProductReview | undefined;
  images: ExtendedProductReviewImage[] | null;
};

export type ActiveImage = { imageId: string; reviewId: string } | null;

export const useReviewImageModal = (): ReviewImageModalReturnValue => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeImage, setActiveImage] = useState<ActiveImage>(null);
  const [hasBackButton, setHasBackButton] = useState(false);
  const { images, reviews } = useProductReviewsImages();
  const activeReview = useMemo(() => {
    if (!reviews || !activeImage) {
      return undefined;
    }

    return reviews.find((review) => review.id === activeImage.reviewId);
  }, [reviews, activeImage]);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onImageChange = useCallback(
    (activeImage: ActiveImage, withBackButton?: boolean) => {
      setHasBackButton(withBackButton ?? false);
      setActiveImage(activeImage);

      if (activeImage) {
        openModal();
      }
    },
    [openModal]
  );

  useEffect(() => {
    if (!isOpen && activeImage !== null) {
      const timeoutId = setTimeout(() => setActiveImage(null), 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, activeImage]);

  return {
    isOpen,
    hasBackButton: activeImage !== null && hasBackButton,
    openModal,
    closeModal,
    onImageChange,
    activeImage,
    activeReview,
    images,
  };
};
